// components/ui/Select.tsx
import React, { forwardRef, useCallback, useMemo } from "react";

type SelectOption = string | {
  value: string;
  label?: string;
  disabled?: boolean;
};

type SelectProps = {
  id?: string;
  value?: string;
  options: (SelectOption | undefined)[];
  onChange: (value: string) => void;
  label?: string;
  isModified?: boolean; // Highlight if the value is modified
  style?: React.CSSProperties; // Optional custom styles
  disabled?: boolean;
};

const normalizeOption = (option: SelectOption | undefined): { value: string; label: string; disabled?: boolean } => {
  if (typeof option === 'string') return { value: option, label: option };
  return {
    value: option?.value ?? '',
    label: option?.label ?? option?.value ?? '',
    disabled: option?.disabled,
  };
};

const CONTAINER_STYLE: React.CSSProperties = { display: "flex", flexDirection: "column", gap: "4px" };

const BASE_SELECT_STYLE: React.CSSProperties = {
  paddingLeft: "8px",
  paddingRight: "8px",
  paddingTop: "4px",
  paddingBottom: "4px",
  borderRadius: "7px",
  backgroundColor: "#262626",
  color: "#909090",
};

const SelectComponent = forwardRef<HTMLSelectElement, SelectProps>(
  ({ id, value, options, onChange, label, isModified = false, style, disabled = false }, ref) => {
    const selectStyle = useMemo<React.CSSProperties>(() => ({
      ...BASE_SELECT_STYLE,
      border: isModified ? "2px solid #888888" : "0px solid #333333",
      ...style,
    }), [isModified, style]);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLSelectElement>) => onChange(event.target.value),
      [onChange]
    );

    return (
      <div style={CONTAINER_STYLE}>
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-white">
            {label}
          </label>
        )}
        <select
          id={id}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          style={selectStyle}
          ref={ref}
          data-fater-custom-select="true"
        >
          {options.map((option, index) => {
            const normalizedOption = normalizeOption(option);
            return (
              <option key={`${normalizedOption.value}:${index}`} value={normalizedOption.value} disabled={normalizedOption.disabled}>
                {normalizedOption.label}
              </option>
            );
          })}
        </select>
      </div>
    );
  }
);

SelectComponent.displayName = "Select";

const Select = React.memo(SelectComponent);

export default Select;
